"use client";

import { useState } from "react";

import type { Route } from "next";
import Image from "next/image";
import Link from "next/link";

import { Button } from "@unreserved/ui/components/button";

import { IMAGES } from "@/app/_libs/constants/images";
import {
  contactInfo,
  footerData,
  navigationLinks,
} from "@/app/_libs/data/landing-page/footer-data";

export function LandingFooter() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <footer className="relative flex w-full flex-col items-start rounded-t bg-black px-4 pt-12 pb-6 md:px-8 md:pt-16 lg:px-[7%] lg:py-[4%]">
      <div className="relative mx-auto flex w-[90%] shrink-0 flex-col items-start gap-8 md:gap-12 lg:w-full lg:max-w-full lg:gap-14">
        {/* Logo and Description Section */}
        <div className="relative flex w-full shrink-0 flex-col items-start gap-6">
          <div className="relative flex w-full shrink-0 flex-col items-start gap-4">
            <div className="relative h-[59px] w-[208px] shrink-0 overflow-hidden">
              <Image
                src={IMAGES.LOGO_WHITE_BG}
                alt="Unreserved Logo"
                width={219}
                height={59}
                className="object-contain"
              />
            </div>
            <p className="relative shrink-0 text-base leading-normal font-medium text-white md:text-lg">
              {isExpanded ? footerData?.fullDescription : footerData?.description}
              <Button
                onClick={() => setIsExpanded(!isExpanded)}
                variant="link"
                aria-expanded={isExpanded}
                aria-label={isExpanded ? "Collapse description" : "Expand description"}
                className="text-primary-400 cursor-pointer text-base leading-normal font-medium hover:underline">
                {isExpanded ? "Read Less" : "Read More"}
              </Button>
            </p>
          </div>

          {/* Contact Information */}
          <div className="relative flex w-full shrink-0 flex-col items-start gap-6 md:flex-row md:items-center md:justify-between md:gap-6 lg:gap-20">
            {contactInfo?.map((contact) => {
              const content = (
                <>
                  <Image
                    src={contact?.icon}
                    alt={contact?.alt}
                    width={24}
                    height={24}
                    className="shrink-0"
                  />
                  <div className="flex flex-col items-start gap-0.5 text-white">
                    <p className="text-sm font-normal">{contact?.label}</p>
                    <p className="text-base font-medium">{contact?.value}</p>
                  </div>
                </>
              );

              if (contact?.href) {
                return (
                  <Link
                    key={contact?.label}
                    href={contact?.href}
                    className="flex flex-row items-center gap-3 transition-opacity hover:opacity-80">
                    {content}
                  </Link>
                );
              }

              return (
                <div key={contact?.label} className="flex flex-row items-center gap-3">
                  {content}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Section - Navigation and Copyright */}
        <div className="border-border-300 relative flex w-full shrink-0 flex-col items-start justify-between gap-4 border-t px-0 py-6 text-sm leading-normal font-medium text-white md:h-[65px] md:flex-row md:items-center md:gap-6 md:border-0 lg:gap-8 lg:border-0 xl:border-t">
          <nav aria-label="Footer navigation">
            <div className="flex flex-col flex-wrap items-start gap-4 md:flex-row md:items-center md:gap-6 lg:gap-8">
              {navigationLinks?.map((link) => {
                return (
                  <Link
                    key={link?.href}
                    href={link?.href as Route<string>}
                    className="hover:text-accent-blue relative shrink-0 transition-colors">
                    {link?.label}
                  </Link>
                );
              })}
            </div>
          </nav>
          <p className="relative shrink-0 text-center md:text-right">{footerData?.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
